<template>
  <card-component
    truncate
    class="resource question"
    size="xsmall"
    :class="[
      {
        moe: checkIfMoe,
        community: checkIfCommunity,
      },
      selectedClass,
    ]"
    @on-click="handlePrimaryCardAction(resourceData.uuid)"
  >
    <template #label>
      <span class="location">{{ location(resourceData.resourceDomain) }}</span>
    </template>
    <template #thumbnail>
      <component :is="thumbnail" class="thumbnail" />
    </template>

    <template #header>
      <h6 class="title">{{ resourceData.title }}</h6>
    </template>
    <template #metadata>
      <li>Rubric</li>
      <li v-if="subjectLabel" class="subject">{{ subjectLabel }}</li>
    </template>
    <template #actions>
      <cv-button
        v-if="isSelection"
        class="icon-button view-details"
        kind="ghost"
        @click="handleViewDetails"
      >
        <svg-icon name="Details24" kind="action" />
      </cv-button>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { isEmpty, isNull } from "@utils/var.check";
import SearchResourceDetailContentSubpage from "@modules/search/components/SearchResourceDetailContentSubpage.vue";
import {
  ADD_FROM_LOCATION,
  NAMESPACE,
  RESOURCE_TYPE_VALUE,
} from "@modules/search/service/search.constant";
import RubricsThumbnail from "@images/lesson/Question/Rubrics.svg";
import SearchSelectionMixin from "@modules/search/mixin/search.selection.mixin";
import ResourceMixin from "@plugins/common/mixin/resource.mixin";
import { formatSubjectTitleCase, formatSubjectWordsTitleCase } from "@utils/subject.utils.ts";
import { computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useVm } from "@utils/vm.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

// Mixin
defineOptions({
  mixins: [SearchSelectionMixin, ResourceMixin],
});

// Inject
const searchContext = inject("searchContext");

// Props
interface Props {
  resourceData?: Record<string, unknown>;
  fileType?: string | null;
  size?: string;
  searchFilters?: Record<string, unknown>;
  isGlobalSearch?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  resourceData: null,
  fileType: null,
  size: "xsmall",
  searchFilters: () => ({}),
  isGlobalSearch: false,
});

// Emits
const emit = defineEmits<{
  actionBack: [];
  selectionSaved: [data: unknown];
  resourceUnstarred: [data: unknown];
}>();

// Composables
const { t } = useI18n();
const authStore = useStore();
const { vm } = useVm();
const contentSubpageInstance = useContentSubpageInstance();

// Data
const processedData = ref(props.resourceData);
const subjectLevelList = ref([]);

// Computed
const isStudent = computed(() => authStore.getters[`${NAMESPACE.AUTH}/isStudent`]);

const thumbnail = computed(() => {
  return RubricsThumbnail;
});

const isAddFromLibraryOrStarredResources = computed(() => {
  if (!searchContext.addFromLocation) return false;
  return (
    searchContext.addFromLocation === ADD_FROM_LOCATION.LIBRARY ||
    searchContext.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES
  );
});

const hasSelectAction = computed(() => {
  return !isStudent.value && isAddFromLibraryOrStarredResources.value;
});

const subjectLabel = computed(() => {
  if (isEmpty(subjectLevelList.value)) {
    return "";
  }
  return formatSubjectTitleCase(subjectLevelList.value[0].subjectName);
});

// Methods
const handleViewDetails = () => {
  contentSubpageInstance.show(
    SearchResourceDetailContentSubpage,
    {
      baseTitle: props.isGlobalSearch ? [props.resourceData.title] : vm.subpageTitle,
      title: t("grading_rubric_details_label"),
      uuid: props.resourceData.uuid,
      hasParentSubpage: !props.isGlobalSearch,
      type: RESOURCE_TYPE_VALUE.RUBRIC,
      location: vm.location(props.resourceData.resourceDomain),
      hasSelectAction: hasSelectAction.value,
      addFromLocation: searchContext.addFromLocation,
      addFromMpqChildLayer: searchContext.addFromMpqChildLayer,
      addFromMpqSubLayer: searchContext.addFromMpqSubLayer,
      searchFilters: props.searchFilters,
      isGlobalSearch: props.isGlobalSearch,
      gradingRubric: props.resourceData,
      actionBack: () => {
        emit("actionBack");
      },
    },
    {
      resourceSelected: handleChooseSelection,
      resourceUnstarred: handleResourceUnstarred,
    },
  );
};

const handleResourceUnstarred = () => {
  if (searchContext?.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES) {
    emit("resourceUnstarred", props.resourceData.uuid);
  }
};

const sortResourceTaggingsAndGenerateSubjectLevelList = () => {
  if (isEmpty(processedData.value?.resourceTaggings)) return;

  let resourceTaggings = processedData.value.resourceTaggings;

  if (isEmpty(resourceTaggings)) {
    return;
  }

  resourceTaggings = resourceTaggings.filter((rt) => !isNull(rt.subjectName)).slice(0, 3);

  if (isEmpty(resourceTaggings)) {
    return;
  }

  // Sort the resourceTaggings array by subjectName and then levelAndStreamName
  resourceTaggings.sort((a, b) => {
    // Sort by subjectName
    const subjectComparison = (a.subjectName || "").localeCompare(b.subjectName || "");
    if (subjectComparison !== 0) {
      return subjectComparison;
    }
    // If subjectName is the same, sort by levelAndStreamName
    return (a.levelAndStreamName || "").localeCompare(b.levelAndStreamName || "");
  });

  for (const tagging of resourceTaggings) {
    if (tagging.subjectName !== null || tagging.levelAndStreamName !== null) {
      const formattedSubjectName = tagging.subjectName
        ? formatSubjectWordsTitleCase(tagging.subjectName)
        : null;

      const pair = {
        subjectKey: tagging.subjectKey,
        subjectName: formattedSubjectName,
        levelAndStreamName: tagging.levelAndStreamName,
        subjectId: tagging.subjectId,
        levelAndStreamId: tagging.levelAndStreamId,
      };

      subjectLevelList.value.push(pair);
    }
  }
  processedData.value.resourceTaggings = resourceTaggings;
};

const handlePrimaryCardAction = (data) => {
  if (props.isGlobalSearch) {
    handleViewDetails();
  } else {
    handleResourceSelected(data);
  }
};

const handleResourceSelected = (data) => {
  const event = {
    data: {
      uuid: data,
    },
  };
  emit("selectionSaved", event);
};

const handleChooseSelection = (data) => {
  emit("selectionSaved", data);
};

// Lifecycle hooks
onMounted(() => {
  if (props.searchFilters) {
    sortResourceTaggingsAndGenerateSubjectLevelList();
  }
});
</script>
